import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsent from "@/components/CookieConsent";
import FirstVisitPopup from "@/components/FirstVisitPopup";

// Critical path - eagerly loaded
import Index from "./pages/Index";

// Lazy loaded pages
const SobreNos = lazy(() => import("./pages/SobreNos"));
const Servicos = lazy(() => import("./pages/Servicos"));
const Segmentos = lazy(() => import("./pages/Segmentos"));
const Locais = lazy(() => import("./pages/Locais"));
const ControleIntegradoPragas = lazy(() => import("./pages/ControleIntegradoPragas"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AtendimentoImediato = lazy(() => import("./pages/AtendimentoImediato"));
const CertificacoesPage = lazy(() => import("./pages/CertificacoesPage"));
const ContatoPage = lazy(() => import("./pages/ContatoPage"));
const TrabalheConosco = lazy(() => import("./pages/TrabalheConosco"));
const Orcamento = lazy(() => import("./pages/Orcamento"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Obrigado = lazy(() => import("./pages/Obrigado"));
const Auth = lazy(() => import("./pages/Auth"));
const BlogAdmin = lazy(() => import("./pages/admin/BlogAdmin"));
const ApplicationsAdmin = lazy(() => import("./pages/admin/ApplicationsAdmin"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Services
const DedetizacaoGeral = lazy(() => import("./pages/services/DedetizacaoGeral"));
const Descupinizacao = lazy(() => import("./pages/services/Descupinizacao"));
const Desratizacao = lazy(() => import("./pages/services/Desratizacao"));
const Desinsetizacao = lazy(() => import("./pages/services/Desinsetizacao"));
const ManejoAbelhas = lazy(() => import("./pages/services/ManejoAbelhas"));
const Sanitizacao = lazy(() => import("./pages/services/Sanitizacao"));
const DesinfeccaoAreia = lazy(() => import("./pages/services/DesinfeccaoAreia"));
const HigienizacaoReservatorios = lazy(() => import("./pages/services/HigienizacaoReservatorios"));
const Afugentamento = lazy(() => import("./pages/services/Afugentamento"));
const CapinacaoMecanica = lazy(() => import("./pages/services/CapinacaoMecanica"));
const DedetizacaoBaratas = lazy(() => import("./pages/services/DedetizacaoBaratas"));
const DedetizacaoFormigas = lazy(() => import("./pages/services/DedetizacaoFormigas"));
const DedetizacaoEscorpioes = lazy(() => import("./pages/services/DedetizacaoEscorpioes"));
const DedetizacaoMosquitos = lazy(() => import("./pages/services/DedetizacaoMosquitos"));
const DesratizacaoSEO = lazy(() => import("./pages/services/DesratizacaoSEO"));
const DescupinizacaoSEO = lazy(() => import("./pages/services/DescupinizacaoSEO"));
const ControleMorcegos = lazy(() => import("./pages/services/ControleMorcegos"));
const SanitizacaoSEO = lazy(() => import("./pages/services/SanitizacaoSEO"));
const LimpezaCaixaDagua = lazy(() => import("./pages/services/LimpezaCaixaDagua"));
const DedetizacaoPulgasCarrapatos = lazy(() => import("./pages/services/DedetizacaoPulgasCarrapatos"));

// Cities
const Recife = lazy(() => import("./pages/Recife"));
const Olinda = lazy(() => import("./pages/Olinda"));
const JaboataoGuararapes = lazy(() => import("./pages/JaboataoGuararapes"));
const Paulista = lazy(() => import("./pages/Paulista"));
const CaboSantoAgostinho = lazy(() => import("./pages/CaboSantoAgostinho"));
const Camaragibe = lazy(() => import("./pages/Camaragibe"));
const SaoLourencoMata = lazy(() => import("./pages/SaoLourencoMata"));
const AbreuLima = lazy(() => import("./pages/AbreuLima"));
const Igarassu = lazy(() => import("./pages/Igarassu"));
const Caruaru = lazy(() => import("./pages/Caruaru"));
const Gravata = lazy(() => import("./pages/Gravata"));
const SantaCruzCapibaribe = lazy(() => import("./pages/SantaCruzCapibaribe"));
const Toritama = lazy(() => import("./pages/Toritama"));
const SaoVicenteFerrer = lazy(() => import("./pages/SaoVicenteFerrer"));
const Limoeiro = lazy(() => import("./pages/Limoeiro"));
const VitoriaSantoAntao = lazy(() => import("./pages/VitoriaSantoAntao"));
const JoaoAlfredo = lazy(() => import("./pages/JoaoAlfredo"));
const Orobo = lazy(() => import("./pages/Orobo"));
const Paudalho = lazy(() => import("./pages/Paudalho"));
const Carpina = lazy(() => import("./pages/Carpina"));
const Cumaru = lazy(() => import("./pages/Cumaru"));
const LagoaItaenga = lazy(() => import("./pages/LagoaItaenga"));
const GloriaGoita = lazy(() => import("./pages/GloriaGoita"));
const Pombos = lazy(() => import("./pages/Pombos"));
const BomJardim = lazy(() => import("./pages/BomJardim"));
const BeloJardim = lazy(() => import("./pages/BeloJardim"));
const FreiMiguelinho = lazy(() => import("./pages/FreiMiguelinho"));
const SantaMariaCambuca = lazy(() => import("./pages/SantaMariaCambuca"));
const Surubim = lazy(() => import("./pages/Surubim"));
const Casinhas = lazy(() => import("./pages/Casinhas"));
const Bezerros = lazy(() => import("./pages/Bezerros"));
const BomConselho = lazy(() => import("./pages/BomConselho"));
const BrejoMadreDeus = lazy(() => import("./pages/BrejoMadreDeus"));
const BuenosAires = lazy(() => import("./pages/BuenosAires"));
const ChaAlegria = lazy(() => import("./pages/ChaAlegria"));
const ChaGrande = lazy(() => import("./pages/ChaGrande"));
const Goiana = lazy(() => import("./pages/Goiana"));
const Ipojuca = lazy(() => import("./pages/Ipojuca"));
const Jatauba = lazy(() => import("./pages/Jatauba"));
const LagoaCarro = lazy(() => import("./pages/LagoaCarro"));
const Moreno = lazy(() => import("./pages/Moreno"));
const NazareMata = lazy(() => import("./pages/NazareMata"));
const FeiraNova = lazy(() => import("./pages/FeiraNova"));
const Timbauba = lazy(() => import("./pages/Timbauba"));
const Vertentes = lazy(() => import("./pages/Vertentes"));
const VertenteLerio = lazy(() => import("./pages/VertenteLerio"));
const Escada = lazy(() => import("./pages/Escada"));
const Vicencia = lazy(() => import("./pages/Vicencia"));
const Passira = lazy(() => import("./pages/Passira"));
const Amaraji = lazy(() => import("./pages/Amaraji"));
const Bonito = lazy(() => import("./pages/Bonito"));
const Tracunhaem = lazy(() => import("./pages/Tracunhaem"));
const Alianca = lazy(() => import("./pages/Alianca"));
const Pocinhos = lazy(() => import("./pages/Pocinhos"));
const Machados = lazy(() => import("./pages/Machados"));
const Umbuzeiro = lazy(() => import("./pages/Umbuzeiro"));
const Salgadinho = lazy(() => import("./pages/Salgadinho"));
const Macaparana = lazy(() => import("./pages/Macaparana"));
const Itaquitinga = lazy(() => import("./pages/Itaquitinga"));
const CamocimSaoFelix = lazy(() => import("./pages/CamocimSaoFelix"));
const RiachoAlmas = lazy(() => import("./pages/RiachoAlmas"));
const Saire = lazy(() => import("./pages/Saire"));

// Neighborhoods
const MauricioNassauCaruaru = lazy(() => import("./pages/neighborhoods/MauricioNassauCaruaru"));
const UniversitarioCaruaru = lazy(() => import("./pages/neighborhoods/UniversitarioCaruaru"));
const IndianopolisCaruaru = lazy(() => import("./pages/neighborhoods/IndianopolisCaruaru"));
const ReservaPaivaCabo = lazy(() => import("./pages/neighborhoods/ReservaPaivaCabo"));
const GarapuCabo = lazy(() => import("./pages/neighborhoods/GarapuCabo"));
const EnseadaCoraisCabo = lazy(() => import("./pages/neighborhoods/EnseadaCoraisCabo"));
const BairroNobreVitoria = lazy(() => import("./pages/neighborhoods/BairroNobreVitoria"));
const RedencaoVitoria = lazy(() => import("./pages/neighborhoods/RedencaoVitoria"));
const UniversitarioVitoria = lazy(() => import("./pages/neighborhoods/UniversitarioVitoria"));
const AldeiaSaoLourenco = lazy(() => import("./pages/neighborhoods/AldeiaSaoLourenco"));
const MuribaraSaoLourenco = lazy(() => import("./pages/neighborhoods/MuribaraSaoLourenco"));
const CentroSaoLourenco = lazy(() => import("./pages/neighborhoods/CentroSaoLourenco"));
const NovoSantoAgostinhoSC = lazy(() => import("./pages/neighborhoods/NovoSantoAgostinhoSC"));
const CentroSantaCruz = lazy(() => import("./pages/neighborhoods/CentroSantaCruz"));
const MalaquiasCardosoSC = lazy(() => import("./pages/neighborhoods/MalaquiasCardosoSC"));
const AlpesSuicosGravata = lazy(() => import("./pages/neighborhoods/AlpesSuicosGravata"));
const WintervilleGravata = lazy(() => import("./pages/neighborhoods/WintervilleGravata"));
const PradoGravata = lazy(() => import("./pages/neighborhoods/PradoGravata"));
const CentroGoiana = lazy(() => import("./pages/neighborhoods/CentroGoiana"));
const PontaPedrasGoiana = lazy(() => import("./pages/neighborhoods/PontaPedrasGoiana"));
const FlexeirasGoiana = lazy(() => import("./pages/neighborhoods/FlexeirasGoiana"));
const CajaCarpina = lazy(() => import("./pages/neighborhoods/CajaCarpina"));
const SantoAntonioCarpina = lazy(() => import("./pages/neighborhoods/SantoAntonioCarpina"));
const IpsepCarpina = lazy(() => import("./pages/neighborhoods/IpsepCarpina"));
const AldeiaPaudalho = lazy(() => import("./pages/neighborhoods/AldeiaPaudalho"));
const ChaCruzPaudalho = lazy(() => import("./pages/neighborhoods/ChaCruzPaudalho"));
const CentroPaudalho = lazy(() => import("./pages/neighborhoods/CentroPaudalho"));
const SaoSebastiaoSurubim = lazy(() => import("./pages/neighborhoods/SaoSebastiaoSurubim"));
const CentroSurubim = lazy(() => import("./pages/neighborhoods/CentroSurubim"));
const CoqueiroSurubim = lazy(() => import("./pages/neighborhoods/CoqueiroSurubim"));

// Segments
const ControlePragasIndustrias = lazy(() => import("./pages/segments/ControlePragasIndustrias"));
const DedetizacaoRestaurantes = lazy(() => import("./pages/segments/DedetizacaoRestaurantes"));
const ControlePragasSaude = lazy(() => import("./pages/segments/ControlePragasSaude"));
const DedetizacaoCondominios = lazy(() => import("./pages/segments/DedetizacaoCondominios"));
const DedetizacaoEscolas = lazy(() => import("./pages/segments/DedetizacaoEscolas"));
const DedetizacaoHoteis = lazy(() => import("./pages/segments/DedetizacaoHoteis"));
const DedetizacaoSupermercados = lazy(() => import("./pages/segments/DedetizacaoSupermercados"));
const ControlePragasTextil = lazy(() => import("./pages/segments/ControlePragasTextil"));

// Landing Pages
const LpCondominiosPernambuco = lazy(() => import("./pages/LpCondominiosPernambuco"));
const LpUrgenciaEscorpioes = lazy(() => import("./pages/LpUrgenciaEscorpioes"));
const LpLimpezaCaixasIndustriais = lazy(() => import("./pages/LpLimpezaCaixasIndustriais"));

// Test page
const TestYoutubeIntegration = lazy(() => import("./pages/TestYoutubeIntegration"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <CookieConsent />
        <FirstVisitPopup />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Level 1 - Institutional */}
            <Route path="/" element={<Index />} />
            <Route path="/sobre-nos" element={<SobreNos />} />
            <Route path="/servicos" element={<Servicos />} />
            <Route path="/segmentos" element={<Segmentos />} />
            <Route path="/locais" element={<Locais />} />
            <Route path="/controle-integrado-pragas" element={<ControleIntegradoPragas />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/atendimento-imediato" element={<AtendimentoImediato />} />
            <Route path="/certificacoes" element={<CertificacoesPage />} />
            <Route path="/contato" element={<ContatoPage />} />
            <Route path="/trabalhe-conosco" element={<TrabalheConosco />} />
            <Route path="/orcamento" element={<Orcamento />} />
            <Route path="/privacidade" element={<Privacidade />} />
            <Route path="/obrigado" element={<Obrigado />} />

            {/* Auth & Admin */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/admin/blog" element={<BlogAdmin />} />
            <Route path="/admin/candidaturas" element={<ApplicationsAdmin />} />

            {/* Old service routes */}
            <Route path="/servicos/dedetizacao-geral" element={<DedetizacaoGeral />} />
            <Route path="/servicos/descupinizacao" element={<Descupinizacao />} />
            <Route path="/servicos/desratizacao" element={<Desratizacao />} />
            <Route path="/servicos/desinsetizacao" element={<Desinsetizacao />} />
            <Route path="/servicos/manejo-abelhas" element={<ManejoAbelhas />} />
            <Route path="/servicos/sanitizacao" element={<Sanitizacao />} />
            <Route path="/servicos/desinfeccao-areia" element={<DesinfeccaoAreia />} />
            <Route path="/servicos/higienizacao-reservatorios" element={<HigienizacaoReservatorios />} />
            <Route path="/servicos/afugentamento" element={<Afugentamento />} />
            <Route path="/servicos/capinacao-mecanica" element={<CapinacaoMecanica />} />

            {/* Level 3 - Segments (B2B) */}
            <Route path="/controle-pragas-industrias" element={<ControlePragasIndustrias />} />
            <Route path="/dedetizacao-restaurantes" element={<DedetizacaoRestaurantes />} />
            <Route path="/controle-pragas-saude" element={<ControlePragasSaude />} />
            <Route path="/dedetizacao-condominios" element={<DedetizacaoCondominios />} />
            <Route path="/dedetizacao-escolas" element={<DedetizacaoEscolas />} />
            <Route path="/dedetizacao-hoteis-pousadas" element={<DedetizacaoHoteis />} />
            <Route path="/dedetizacao-supermercados" element={<DedetizacaoSupermercados />} />
            <Route path="/controle-pragas-textil" element={<ControlePragasTextil />} />

            {/* SEO Service pages */}
            <Route path="/dedetizacao-baratas" element={<DedetizacaoBaratas />} />
            <Route path="/dedetizacao-formigas" element={<DedetizacaoFormigas />} />
            <Route path="/dedetizacao-escorpioes" element={<DedetizacaoEscorpioes />} />
            <Route path="/dedetizacao-mosquitos" element={<DedetizacaoMosquitos />} />
            <Route path="/desratizacao" element={<DesratizacaoSEO />} />
            <Route path="/descupinizacao" element={<DescupinizacaoSEO />} />
            <Route path="/controle-morcegos" element={<ControleMorcegos />} />
            <Route path="/sanitizacao" element={<SanitizacaoSEO />} />
            <Route path="/limpeza-caixa-dagua" element={<LimpezaCaixaDagua />} />
            <Route path="/dedetizacao-pulgas-carrapatos" element={<DedetizacaoPulgasCarrapatos />} />

            {/* Level 4 - SEO Local Cities */}
            <Route path="/dedetizacao-em-caruaru" element={<Caruaru />} />
            <Route path="/dedetizacao-no-cabo" element={<CaboSantoAgostinho />} />
            <Route path="/dedetizacao-em-vitoria" element={<VitoriaSantoAntao />} />
            <Route path="/dedetizacao-sao-lourenco" element={<SaoLourencoMata />} />
            <Route path="/dedetizacao-santa-cruz" element={<SantaCruzCapibaribe />} />
            <Route path="/dedetizacao-em-gravata" element={<Gravata />} />
            <Route path="/dedetizacao-em-goiana" element={<Goiana />} />
            <Route path="/dedetizacao-em-carpina" element={<Carpina />} />
            <Route path="/dedetizacao-em-paudalho" element={<Paudalho />} />
            <Route path="/dedetizacao-em-surubim" element={<Surubim />} />
            <Route path="/dedetizacao-em-limoeiro" element={<Limoeiro />} />
            <Route path="/dedetizacao-em-bezerros" element={<Bezerros />} />
            <Route path="/dedetizacao-em-escada" element={<Escada />} />
            <Route path="/dedetizacao-em-toritama" element={<Toritama />} />
            <Route path="/dedetizacao-em-bom-jardim" element={<BomJardim />} />
            <Route path="/dedetizacao-nazare-da-mata" element={<NazareMata />} />
            <Route path="/dedetizacao-em-timbauba" element={<Timbauba />} />
            <Route path="/dedetizacao-em-vicencia" element={<Vicencia />} />
            <Route path="/dedetizacao-joao-alfredo" element={<JoaoAlfredo />} />
            <Route path="/dedetizacao-em-passira" element={<Passira />} />
            <Route path="/dedetizacao-lagoa-do-carro" element={<LagoaCarro />} />
            <Route path="/dedetizacao-lagoa-de-itaenga" element={<LagoaItaenga />} />
            <Route path="/dedetizacao-feira-nova" element={<FeiraNova />} />
            <Route path="/dedetizacao-gloria-do-goita" element={<GloriaGoita />} />
            <Route path="/dedetizacao-cha-grande" element={<ChaGrande />} />
            <Route path="/dedetizacao-cha-de-alegria" element={<ChaAlegria />} />
            <Route path="/dedetizacao-em-pombos" element={<Pombos />} />
            <Route path="/dedetizacao-em-amaraji" element={<Amaraji />} />
            <Route path="/dedetizacao-em-moreno" element={<Moreno />} />
            <Route path="/dedetizacao-em-cumaru" element={<Cumaru />} />
            <Route path="/dedetizacao-em-orobo" element={<Orobo />} />
            <Route path="/dedetizacao-em-bonito" element={<Bonito />} />
            <Route path="/dedetizacao-em-tracunhaem" element={<Tracunhaem />} />
            <Route path="/dedetizacao-em-alianca" element={<Alianca />} />
            <Route path="/dedetizacao-em-pocinhos" element={<Pocinhos />} />
            <Route path="/dedetizacao-buenos-aires" element={<BuenosAires />} />
            <Route path="/dedetizacao-em-machados" element={<Machados />} />
            <Route path="/dedetizacao-frei-miguelinho" element={<FreiMiguelinho />} />
            <Route path="/dedetizacao-santa-maria-cambuca" element={<SantaMariaCambuca />} />
            <Route path="/dedetizacao-em-umbuzeiro" element={<Umbuzeiro />} />
            <Route path="/dedetizacao-em-salgadinho" element={<Salgadinho />} />
            <Route path="/dedetizacao-em-macaparana" element={<Macaparana />} />
            <Route path="/dedetizacao-em-itaquitinga" element={<Itaquitinga />} />
            <Route path="/dedetizacao-vertente-do-lerio" element={<VertenteLerio />} />
            <Route path="/dedetizacao-em-casinhas" element={<Casinhas />} />
            <Route path="/dedetizacao-camocim-sao-felix" element={<CamocimSaoFelix />} />
            <Route path="/dedetizacao-riacho-das-almas" element={<RiachoAlmas />} />
            <Route path="/dedetizacao-em-saire" element={<Saire />} />

            {/* Level 5 - Hyper-local SEO (Premium Neighborhoods) */}
            <Route path="/mauricio-de-nassau" element={<MauricioNassauCaruaru />} />
            <Route path="/universitario" element={<UniversitarioCaruaru />} />
            <Route path="/indianopolis" element={<IndianopolisCaruaru />} />
            <Route path="/reserva-do-paiva" element={<ReservaPaivaCabo />} />
            <Route path="/garapu" element={<GarapuCabo />} />
            <Route path="/enseada-dos-corais" element={<EnseadaCoraisCabo />} />
            <Route path="/bairro-nobre" element={<BairroNobreVitoria />} />
            <Route path="/redencao" element={<RedencaoVitoria />} />
            <Route path="/universitario-vitoria" element={<UniversitarioVitoria />} />
            <Route path="/aldeia" element={<AldeiaSaoLourenco />} />
            <Route path="/muribara" element={<MuribaraSaoLourenco />} />
            <Route path="/centro-sao-lourenco" element={<CentroSaoLourenco />} />
            <Route path="/novo-santo-agostinho" element={<NovoSantoAgostinhoSC />} />
            <Route path="/centro-santa-cruz" element={<CentroSantaCruz />} />
            <Route path="/malaquias-cardoso" element={<MalaquiasCardosoSC />} />
            <Route path="/alpes-suicos" element={<AlpesSuicosGravata />} />
            <Route path="/winterville" element={<WintervilleGravata />} />
            <Route path="/prado" element={<PradoGravata />} />
            <Route path="/centro-goiana" element={<CentroGoiana />} />
            <Route path="/ponta-de-pedras" element={<PontaPedrasGoiana />} />
            <Route path="/flexeiras" element={<FlexeirasGoiana />} />
            <Route path="/caja" element={<CajaCarpina />} />
            <Route path="/santo-antonio" element={<SantoAntonioCarpina />} />
            <Route path="/ipsep" element={<IpsepCarpina />} />
            <Route path="/aldeia-paudalho" element={<AldeiaPaudalho />} />
            <Route path="/cha-de-cruz" element={<ChaCruzPaudalho />} />
            <Route path="/centro-paudalho" element={<CentroPaudalho />} />
            <Route path="/sao-sebastiao" element={<SaoSebastiaoSurubim />} />
            <Route path="/centro-surubim" element={<CentroSurubim />} />
            <Route path="/coqueiro" element={<CoqueiroSurubim />} />

            {/* Old city routes - redirect to new SEO URLs */}
            <Route path="/caruaru" element={<Navigate to="/dedetizacao-em-caruaru" replace />} />
            <Route path="/cabo-santo-agostinho" element={<Navigate to="/dedetizacao-no-cabo" replace />} />
            <Route path="/vitoria-santo-antao" element={<Navigate to="/dedetizacao-em-vitoria" replace />} />
            <Route path="/sao-lourenco-mata" element={<Navigate to="/dedetizacao-sao-lourenco" replace />} />
            <Route path="/santa-cruz-capibaribe" element={<Navigate to="/dedetizacao-santa-cruz" replace />} />
            <Route path="/gravata" element={<Navigate to="/dedetizacao-em-gravata" replace />} />
            <Route path="/goiana" element={<Navigate to="/dedetizacao-em-goiana" replace />} />
            <Route path="/carpina" element={<Navigate to="/dedetizacao-em-carpina" replace />} />
            <Route path="/paudalho" element={<Navigate to="/dedetizacao-em-paudalho" replace />} />
            <Route path="/surubim" element={<Navigate to="/dedetizacao-em-surubim" replace />} />
            <Route path="/limoeiro" element={<Navigate to="/dedetizacao-em-limoeiro" replace />} />
            <Route path="/bezerros" element={<Navigate to="/dedetizacao-em-bezerros" replace />} />
            <Route path="/toritama" element={<Navigate to="/dedetizacao-em-toritama" replace />} />
            <Route path="/bom-jardim" element={<Navigate to="/dedetizacao-em-bom-jardim" replace />} />
            <Route path="/nazare-mata" element={<Navigate to="/dedetizacao-nazare-da-mata" replace />} />
            <Route path="/timbauba" element={<Navigate to="/dedetizacao-em-timbauba" replace />} />
            <Route path="/joao-alfredo" element={<Navigate to="/dedetizacao-joao-alfredo" replace />} />
            <Route path="/lagoa-carro" element={<Navigate to="/dedetizacao-lagoa-do-carro" replace />} />
            <Route path="/lagoa-itaenga" element={<Navigate to="/dedetizacao-lagoa-de-itaenga" replace />} />
            <Route path="/feira-nova" element={<Navigate to="/dedetizacao-feira-nova" replace />} />
            <Route path="/areas/feira-nova" element={<Navigate to="/dedetizacao-feira-nova" replace />} />
            <Route path="/gloria-goita" element={<Navigate to="/dedetizacao-gloria-do-goita" replace />} />
            <Route path="/cha-grande" element={<Navigate to="/dedetizacao-cha-grande" replace />} />
            <Route path="/cha-alegria" element={<Navigate to="/dedetizacao-cha-de-alegria" replace />} />
            <Route path="/pombos" element={<Navigate to="/dedetizacao-em-pombos" replace />} />
            <Route path="/moreno" element={<Navigate to="/dedetizacao-em-moreno" replace />} />
            <Route path="/cumaru" element={<Navigate to="/dedetizacao-em-cumaru" replace />} />
            <Route path="/orobo" element={<Navigate to="/dedetizacao-em-orobo" replace />} />
            <Route path="/buenos-aires" element={<Navigate to="/dedetizacao-buenos-aires" replace />} />
            <Route path="/frei-miguelinho" element={<Navigate to="/dedetizacao-frei-miguelinho" replace />} />
            <Route path="/santa-maria-cambuca" element={<Navigate to="/dedetizacao-santa-maria-cambuca" replace />} />
            <Route path="/vertente-lerio" element={<Navigate to="/dedetizacao-vertente-do-lerio" replace />} />
            <Route path="/casinhas" element={<Navigate to="/dedetizacao-em-casinhas" replace />} />

            {/* Other existing city pages */}
            <Route path="/recife" element={<Recife />} />
            <Route path="/olinda" element={<Olinda />} />
            <Route path="/jaboatao-guararapes" element={<JaboataoGuararapes />} />
            <Route path="/paulista" element={<Paulista />} />
            <Route path="/camaragibe" element={<Camaragibe />} />
            <Route path="/abreu-lima" element={<AbreuLima />} />
            <Route path="/igarassu" element={<Igarassu />} />
            <Route path="/sao-vicente-ferrer" element={<SaoVicenteFerrer />} />
            <Route path="/belo-jardim" element={<BeloJardim />} />
            <Route path="/bom-conselho" element={<BomConselho />} />
            <Route path="/brejo-madre-deus" element={<BrejoMadreDeus />} />
            <Route path="/ipojuca" element={<Ipojuca />} />
            <Route path="/jatauba" element={<Jatauba />} />
            <Route path="/vertentes" element={<Vertentes />} />
            <Route path="/escada" element={<Escada />} />
            <Route path="/vicencia" element={<Vicencia />} />
            <Route path="/passira" element={<Passira />} />

            {/* Level 7 - Landing Pages (Ads) */}
            <Route path="/lp-condominios-pernambuco" element={<LpCondominiosPernambuco />} />
            <Route path="/lp-urgencia-escorpioes" element={<LpUrgenciaEscorpioes />} />
            <Route path="/lp-limpeza-caixas-industriais" element={<LpLimpezaCaixasIndustriais />} />

            {/* Test */}
            <Route path="/test-youtube" element={<TestYoutubeIntegration />} />

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
